import React, { useState, useEffect, useMemo } from "react";
import {
    TextField,
    InputLabel,
    FormControl,
    MenuItem,
    Select,
    Typography,
    TextareaAutosize,
} from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import actions from "../../Store/actions";
import { useTranslation } from "react-i18next";
import styles from "./styles";

/**
 * Add new dealer with validation
 */
export default ({ validateDealer, setValidateDealer }) => {
    const { auditId } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { typesList, auditUpload } = useSelector((state) => state);
    const { auditSignature } = auditUpload;

    const currentAuditSignature = auditUpload[auditId]
        ? auditUpload[auditId].auditSignature
        : auditSignature;

    // Form states
    const [dealerContactRole, setDealerContactRole] = useState(
        currentAuditSignature.dealerContactRole
    );
    const [dealerContactName, setDealerContactName] = useState(
        currentAuditSignature.dealerContactName
    );
    const [dealerContactEmail, setDealerContactEmail] = useState(
        currentAuditSignature.dealerContactEmail
    );
    const [dealerContactComment, setDealerContactComment] = useState(
        currentAuditSignature.dealerContactComment
    );
    const [errors, setErrors] = useState({});

    // Roles  with types
    const roles = useMemo(() => {
        if (typesList.roles && typesList.roles.data.length > 0) {
            return typesList.roles.data;
        } else return [];
    }, [typesList]);
    const validateField = (name, value) => {
        let tempErrors = { ...errors }; // get the  current errors

        if (name === "dealerContactName") {
            if (!value) tempErrors.dealerContactName = t("errors.nameRequired");
            else delete tempErrors.dealerContactName; // Clear error if valid
        }

        if (name === "dealerContactEmail") {
            const emailPattern =
                /^(?!.*\.\.)(?!.*\.$)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/;
            
            if (!value) {
                tempErrors.dealerContactEmail = t("errors.emailRequired");
            } else if (!emailPattern.test(value)) {
                tempErrors.dealerContactEmail = t("errors.invalidEmail");
            } else {
                delete tempErrors.dealerContactEmail; // Clear error if valid
            }
        }

        if (name === "dealerContactRole") {
            if (!value) tempErrors.dealerContactRole = t("errors.roleRequired");
            else delete tempErrors.dealerContactRole; // Clear error if valid
        }

        setErrors(tempErrors);
    };

    // Check if all fields are valid
    const isFormValid = () => {
        return (
            dealerContactName &&
            dealerContactEmail &&
            dealerContactRole &&
            Object.keys(errors).length === 0
        );
    };

    // useEffect to check form validity whenever a form field changes
    useEffect(() => {
        if (isFormValid()) {
            setValidateDealer(true); // Enable the "Next" button
        } else {
            setValidateDealer(false); // Disable the "Next" button
        }
    }, [dealerContactName, dealerContactEmail, dealerContactRole, errors]);

    // Handle input changes with real-time validation
    const handleDealerNameChange = async (event) => {
        const loadPrevValue = auditUpload[auditId]
            ? {
                  ...currentAuditSignature,
                  ...auditUpload[auditId].auditSignature,
              }
            : currentAuditSignature;

        setDealerContactName(event.target.value);

        await dispatch(
            actions.auditUpload.create.update({
                [auditId]: {
                    auditSignature: {
                        ...loadPrevValue,
                        dealerContactName: event.target.value,
                    },
                },
            })
        );
        validateField("dealerContactName", event.target.value);
    };

    const handleEmailChange = async (event) => {
        const loadPrevValue = auditUpload[auditId]
            ? {
                  ...currentAuditSignature,
                  ...auditUpload[auditId].auditSignature,
              }
            : currentAuditSignature;

        setDealerContactEmail(event.target.value);

        await dispatch(
            actions.auditUpload.create.update({
                [auditId]: {
                    auditSignature: {
                        ...loadPrevValue,
                        dealerContactEmail: event.target.value,
                    },
                },
            })
        );
        validateField("dealerContactEmail", event.target.value);
    };

    const handleRoleChange = async (event) => {
        const loadPrevValue = auditUpload[auditId]
            ? {
                  ...currentAuditSignature,
                  ...auditUpload[auditId].auditSignature,
              }
            : currentAuditSignature;

        setDealerContactRole(event.target.value);

        await dispatch(
            actions.auditUpload.create.update({
                [auditId]: {
                    auditSignature: {
                        ...loadPrevValue,
                        dealerContactRole: event.target.value,
                    },
                },
            })
        );
        validateField("dealerContactRoler", event.target.value);
    };

    const dealerContactCommentChange = async (event) => {
        const loadPrevValue = auditUpload[auditId]
            ? {
                  ...currentAuditSignature,
                  ...auditUpload[auditId].auditSignature,
              }
            : currentAuditSignature;

        setDealerContactComment(event.target.value);

        await dispatch(
            actions.auditUpload.create.update({
                [auditId]: {
                    auditSignature: {
                        ...loadPrevValue,
                        dealerContactComment: event.target.value,
                    },
                },
            })
        );
    };
    return (
        <>
            <FormControl fullWidth margin="dense" component="form">
                <TextField
                    id="dealerContactName"
                    required
                    label={t("dealerContactName")}
                    inputProps={{
                        "data-testid": `dealerContactName`,
                    }}
                    type="text"
                    size="small"
                    value={dealerContactName}
                    onChange={handleDealerNameChange}
                    error={Boolean(errors.dealerContactName)}
                    helperText={errors.dealerContactName}
                />
            </FormControl>
            <FormControl fullWidth margin="dense" component="form">
                <TextField
                    required
                    size="small"
                    fullWidth
                    label={t("dealerContactEmail")}
                    inputProps={{
                        "data-testid": `dealerContactEmail`,
                    }}
                    type="email"
                    value={dealerContactEmail}
                    onChange={handleEmailChange}
                    error={Boolean(errors.dealerContactEmail)}
                    helperText={errors.dealerContactEmail}
                />
            </FormControl>
            <FormControl fullWidth margin="dense">
                <InputLabel id="role-select-label" required>
                    {t("dealerContactRole")}
                </InputLabel>
                <Select
                    required
                    labelId="dealerContactRole"
                    id="role-select"
                    value={dealerContactRole}
                    label={t("dealerContactRole")}
                    onChange={handleRoleChange}
                    error={Boolean(errors.dealerContactRole)}
                >
                    {roles.map((item) => (
                        <MenuItem
                            key={`${item.id}_${item.name}`}
                            value={item.id}
                        >
                            {t(item.name)}
                        </MenuItem>
                    ))}
                </Select>
                {errors.dealerContactRole && (
                    <Typography variant="caption" color="error">
                        {errors.dealerContactRole}
                    </Typography>
                )}
            </FormControl>
            <FormControl
                fullWidth
                margin="dense"
                component="form"
                sx={styles.textArea}
            >
                <TextareaAutosize
                    minRows={2}
                    maxRows={4}
                    placeholder={t("dealerContactComment")}
                    value={dealerContactComment}
                    onChange={dealerContactCommentChange}
                />
            </FormControl>
        </>
    );
};
